import { useMemo, useState } from 'react';

import clsx from 'clsx';

import useSocialProfiles from '@/hooks/swr/useSocialProfiles';
// Hooks
import useActiveSocialProfile from '@/hooks/user/useActiveSocialProfile';
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useIsGroup from '@/utils/hooks/user/useIsGroup';

import PropTypes from 'prop-types';

//lib
import { availableSP } from '@/lib/publishing/collaborate-area/sharing-center';
import { INSTAGRAM } from '@/lib/socialProfile';

import Label from '@/components/base/Label';
import SelectWithSPIcon from '@/components/base/Select/SelectWithSPIcon';
// Components
import SelectFilter from '@/components/base/SelectFilter';
import SpOrSpGroupAvatar from '@/components/composite/SpGroups/SpOrSpGroupAvatar';
import CollaborationLinkSettings from '@/components/pages/scheduler/collaboration/settings/CollaborationLinkSettings';

const SpOption = ({ option, selected, active }) => {
    return (
        <div className="flex items-center" data-testid={`test-${option?.slug}`}>
            <SpOrSpGroupAvatar
                item={option}
                size="sm"
                profileNetworkType={option.social_network_type}
                profileGroupType={true}
            />
            <div className="flex flex-col pl-3.5 text-left">
                <span
                    className={clsx('font-GSemiBold text-sm', {
                        'text-icoBlue': selected || active,
                        'text-icoBlack dark:text-white': !selected && !active
                    })}>
                    {option?.label}
                </span>
            </div>
        </div>
    );
};

SpOption.propTypes = {
    option: PropTypes.object
};

const Form = () => {
    const { t } = useTranslation('scheduler');

    const isGroup = useIsGroup();
    const { activeGroup } = useActiveGroup();
    const { activatedSpGroups } = useSPGroups();

    const { activeSocialProfile } = useActiveSocialProfile();
    const { activatedSocialProfiles } = useSocialProfiles({ revalidateIfStale: false });

    const getSelectedProfile = () => {
        if (isGroup) {
            return { ...activeGroup, group: true };
        }

        if (activeSocialProfile) {
            return activeSocialProfile;
        }

        return activatedSocialProfiles[0];
    };

    const [selectedProfile, setSelectedProfile] = useState(getSelectedProfile());

    const options = useMemo(() => {
        let finalOptions = [];
        let finalProfiles = activatedSocialProfiles;

        finalProfiles = finalProfiles?.filter((social_profile) =>
            availableSP?.includes(social_profile?.social_network_type)
        );

        finalProfiles = [...activatedSpGroups, ...finalProfiles];

        finalProfiles.map((profile) => {
            finalOptions.push({
                label: profile?.name ?? profile.slug,
                value: profile.id,
                group: profile?.name ? true : false,
                ...profile
            });
        });

        return finalOptions;
    }, [activatedSocialProfiles, activatedSpGroups]);

    return (
        <>
            <div className="my-8">
                <Label className="mb-2.5" htmlFor="select-filter-collaboration-settings">
                    <span className="font-GBold text-base text-icoBlack dark:text-white">
                        {t('collaboration.settings.social_profile_or_group_label')}
                    </span>
                </Label>
                <SelectFilter
                    selected={
                        selectedProfile
                            ? options?.filter((profile) =>
                                  selectedProfile?.group
                                      ? profile.id === selectedProfile.id && profile.group
                                      : profile.id === selectedProfile.id
                              )[0]
                            : null
                    }
                    setSelected={setSelectedProfile}
                    idInput="select-filter-collaboration-settings"
                    options={options}
                    button={
                        <SelectWithSPIcon
                            id="select-filter-collaboration-settings"
                            baseStyle="default"
                            profileData={selectedProfile}
                            socialNetworkType={selectedProfile?.social_network_type ?? INSTAGRAM}
                        />
                    }>
                    <SpOption />
                </SelectFilter>
            </div>
            {selectedProfile ? (
                <div className="my-8">
                    <CollaborationLinkSettings selectedProfile={selectedProfile} />
                </div>
            ) : null}
        </>
    );
};

export default Form;
